import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useClinicList } from 'infrastructure/hooks/clinic/use-clinic-list';
import { Clinic } from 'infrastructure/classes/clinic';
import BaseInput from 'components/atoms/base-input';
import SortOrderSelectBox from 'components/molecules/sort-order-select-box';
import { useTranslation } from 'react-i18next';
import BaseSpinWrapper from 'components/atoms/base-spin-wrapper';
import List from 'rc-virtual-list';
import { SortOrder } from 'infrastructure/enums';
import SearchIcon from 'components/atoms/icons/searchIcon';
import SegmentedTabs from 'components/atoms/oneplatform/segmented-tabs';
import EmptySearchIcon from 'components/atoms/icons/emptySearchIcon';

import s from './styles.module.scss';
import ClinicFilterFormListItem from './list-item';

import type { IBaseTab } from 'components/atoms/base-tabs';

type tabs = 'all' | 'selected';

type SetString = Set<string>;

export interface ISelectedClinics {
  all?: string[];
  included?: string[];
}

interface IProps {
  agencyGuid?: string[];
  included?: string[];
  onChange: (props: ISelectedClinics) => void;
  showOnInteractiveTimeDashboard?: boolean;
  useAdminApi?: boolean;
}

const ClinicFilterForm: React.FC<IProps> = ({
  agencyGuid,
  included: includedParam = [],
  showOnInteractiveTimeDashboard,
  useAdminApi,
  onChange,
}) => {
  const { t } = useTranslation();
  const isAdminDashboard = showOnInteractiveTimeDashboard === false;
  const {
    clinics: clinicsResponse,
    loadClinics,
    loading,
    meta,
  } = useClinicList({
    meta: {
      items: 9999999,
      showOnInteractiveTimeDashboard,
      useAdminApi,
    },
  });

  const clinics = useMemo(() => {
    if (!agencyGuid?.length) return clinicsResponse;

    return clinicsResponse.filter((c) =>
      agencyGuid.some((ag) => ag === c.agencyGuid),
    );
  }, [agencyGuid, clinicsResponse]);

  const allClinicGuids = useMemo<SetString>(
    () => new Set(clinics.map((el) => el.guid)),
    [clinics],
  );

  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.ASC);
  const [activeTab, setActiveTab] = useState<tabs>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [includedSet, setIncludedSet] = useState<SetString>(new Set());

  const allClinicSelected = () => {
    return includedSet.size === clinics.length;
  };

  const isAllTab = activeTab === 'all';

  const isChecked = (clinic: Clinic) => includedSet.has(clinic.guid);

  const findParentClinics = (from: Clinic[]) => {
    return from.flatMap((el) =>
      el.parentClinicGuid
        ? clinics.filter((parent) => parent.guid === el.parentClinicGuid)
        : [],
    );
  };

  const filterClinics = (list: SetString) =>
    clinics.filter((clinic) => list.has(clinic.guid));

  const searchInList = (list: Clinic[], searchTerm?: string) => {
    if (!searchTerm) return list;
    const terms = searchTerm.trim().toLowerCase();
    return list.filter((clinic) =>
      clinic.legalname.toLowerCase().includes(terms),
    );
  };

  const groupedClinics = useMemo(() => {
    let clinicsToSort: Clinic[];

    if (activeTab === 'selected') {
      clinicsToSort = filterClinics(includedSet);
    } else {
      clinicsToSort = clinics;
    }

    clinicsToSort = searchInList(clinicsToSort, searchQuery);
    const parentClinics = findParentClinics(clinicsToSort);

    return Clinic.sortByParent([...parentClinics, ...clinicsToSort], sortOrder);
  }, [activeTab, sortOrder, searchQuery, clinics.length, includedSet.size]);

  const showEmptyState = !loading && groupedClinics.length === 0;

  const selectAllLabel = allClinicSelected()
    ? t('controls.deselectAll')
    : t('controls.selectAll');

  const tabs = useMemo<IBaseTab<tabs>[]>(
    () => [
      {
        label: `${t('labels.allClinics')} (${clinics.length})`,
        key: 'all',
      },
      {
        label: `${t('labels.selected')} (${includedSet.size})`,
        key: 'selected',
      },
    ],
    [includedSet.size, clinics.length],
  );

  const normalizeSelectAll = (val: ISelectedClinics): ISelectedClinics => {
    const filters = { ...val };
    const { all } = filters;

    if (
      !isAdminDashboard &&
      Array.isArray(all) &&
      Array.isArray(filters.included)
    ) {
      if (filters.included.length === all.length) {
        filters.included = [];
      }
    }

    if (!filters.included?.length) filters.included = [];
    return filters;
  };

  const setIncludeLists = (inc: SetString) => {
    setIncludedSet(new Set(inc));
    const filters = normalizeSelectAll({
      included: Array.from(inc),
      all: Array.from(allClinicGuids),
    });
    onChange(filters);
  };

  const updateClinics = useCallback(
    (list: tabs | 'all', clinic: Clinic, add: boolean, key?: string) => {
      const updatedIncluded = new Set(includedSet);
      const childGuids = clinic.childClinicsGuid.map(({ guid }) => guid);
      const allowClinicGuids = new Set(clinics.map((c) => c.guid));

      const addGuids = (guids: string[]) => {
        guids.forEach((clinicGuid) => {
          if (allowClinicGuids.has(clinicGuid)) updatedIncluded.add(clinicGuid);
        });
      };

      const removeGuids = (guids: string[]) => {
        guids.forEach((clinicGuid) => {
          if (allowClinicGuids.has(clinicGuid))
            updatedIncluded.delete(clinicGuid);
        });
      };

      if (key === 'all') {
        addGuids([clinic.guid, ...childGuids]);
      } else if (key === 'none') {
        removeGuids([clinic.guid, ...childGuids]);
      } else if (key === 'parent') {
        addGuids([clinic.guid]);
        removeGuids(childGuids);
      } else if (key === 'children') {
        addGuids(childGuids);
        removeGuids([clinic.guid]);
      } else if (list === 'selected' || (list === 'all' && !add)) {
        removeGuids([clinic.guid]);
      } else if (list === 'all' && add) {
        addGuids([clinic.guid]);
      }

      setIncludeLists(updatedIncluded);
    },
    [includedSet, clinics, onChange],
  );

  const onSelect = useCallback(
    (checked: boolean, clinic: Clinic, key?: string) => {
      updateClinics(activeTab, clinic, checked, key);
    },
    [activeTab, updateClinics],
  );

  const selectAll = useCallback(
    (select: boolean, tab: tabs) => {
      const updateState = (inc: SetString) => {
        setIncludeLists(inc);
      };
      const emptySet = new Set([]);

      const tabActions = {
        selected: () => updateState(emptySet),
        all: () =>
          select ? updateState(allClinicGuids) : updateState(emptySet),
      };
      if (tabActions[tab]) tabActions[tab]();
    },
    [clinics, onChange, setIncludeLists],
  );

  useEffect(() => {
    loadClinics(meta);
  }, []);

  useEffect(() => {
    if (includedParam.length) {
      setIncludeLists(new Set(includedParam));
      return;
    }

    setIncludeLists(isAdminDashboard ? new Set() : allClinicGuids);
  }, [
    allClinicGuids.size,
    includedParam.length,
    showOnInteractiveTimeDashboard,
  ]);

  return (
    <BaseSpinWrapper spinning={loading}>
      <div className={s.form}>
        <div className={s.header}>
          <div className={s.block} data-cy="searchClinic-input">
            <BaseInput
              placeholder={t('labels.searchClinic')}
              onChange={(e) => setSearchQuery(e)}
              allowClear
              addonAfter={<SearchIcon />}
            />
          </div>
          <div className={s.block} data-cy="searchClinic-order">
            <SortOrderSelectBox value={sortOrder} onChange={setSortOrder} />
          </div>

          <div className={s.block}>
            <div
              className={s['select-all']}
              onClick={() => selectAll(!allClinicSelected(), activeTab)}
            >
              <span className={s.title}>{selectAllLabel}</span>
            </div>
          </div>
        </div>
        <div className={s.block} data-cy="searchClinic-tabs">
          <SegmentedTabs
            tabs={tabs}
            type="rounded"
            size="small"
            activeKey={activeTab}
            onChange={(tab) => setActiveTab(tab.key)}
          />
        </div>
        <ul className={s.list}>
          {showEmptyState ? (
            <div className={s.empty}>
              <EmptySearchIcon className={s.emptyIcon} />
              {t('labels.noResultFound')}
            </div>
          ) : (
            <List
              data={groupedClinics}
              height={200}
              itemHeight={51}
              itemKey="guid"
            >
              {(clinic: Clinic) => (
                <ClinicFilterFormListItem
                  name={clinic.legalname}
                  showCheckBox={isAllTab || Boolean(isChecked(clinic))}
                  checked={Boolean(isChecked(clinic))}
                  isParent={!clinic.parentClinicGuid}
                  isLast={!clinic.childClinicsGuid.length}
                  withoutParent={clinic.withoutParent}
                  onChange={(checked) => onSelect(checked, clinic)}
                  parentOnChange={(key) => {
                    const checked = key !== 'none';
                    onSelect(checked, clinic, key);
                  }}
                  childrenCount={
                    !clinic.parentClinicGuid &&
                    clinic.childClinicsGuid.length > 0
                      ? clinics.filter(
                          (c) => c.parentClinicGuid === clinic.guid,
                        ).length
                      : undefined
                  }
                />
              )}
            </List>
          )}
        </ul>
      </div>
    </BaseSpinWrapper>
  );
};

export default ClinicFilterForm;
